import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import MovieCard from '@/components/feature/MovieCard';
import { articles } from '@/mocks/articles';
import { movies } from '@/mocks/movies';
import type { Article } from '@/mocks/articles';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const [article, setArticle] = useState<Article | null>(null);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    const found = articles.find(a => a.id === id);
    setArticle(found || null);
  }, [id]);

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16 flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4">
              <i className="ri-article-line text-4xl text-foreground-400"></i>
            </div>
            <p className="text-foreground-500 text-sm">文章未找到</p>
            <Link to="/" className="text-accent-500 text-sm mt-3 inline-block hover:underline cursor-pointer">
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const relatedMovies = movies.filter(m => article.relatedMovieIds.includes(m.id));
  const breadcrumbItems = [
    { label: '首页', href: '/' },
    { label: '电影资讯', href: '/category/all' },
    { label: article.title },
  ];

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Article',
            headline: article.title,
            description: article.summary,
            author: { '@type': 'Person', name: article.author },
            datePublished: article.date,
            image: article.coverImage,
            url: `https://viewhubx.github.io/article/${article.id}`,
            publisher: {
              '@type': 'Organization',
              name: '免费电影在线观看',
            },
          }),
        }}
      />
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16">
          <article>
            <div className="max-w-[900px] mx-auto px-4 md:px-6">
              <Breadcrumb items={breadcrumbItems} />

              <div className="relative aspect-[21/9] rounded-xl overflow-hidden mb-8">
                <img
                  src={article.coverImage}
                  alt={article.title}
                  className="w-full h-full object-cover object-top"
                />
              </div>

              <header className="mb-8">
                <div className="flex items-center gap-3 mb-3 flex-wrap">
                  <span className="px-3 py-1 rounded-full bg-accent-500/15 text-accent-500 text-xs font-medium whitespace-nowrap">
                    {article.category}
                  </span>
                  {article.tags.map(tag => (
                    <span
                      key={tag}
                      className="px-3 py-1 rounded-full bg-background-100 text-foreground-500 text-xs font-medium whitespace-nowrap"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
                <h1 className="text-2xl md:text-3xl font-heading text-foreground-950 leading-tight mb-3">
                  {article.title}
                </h1>
                <div className="flex items-center gap-4 text-sm text-foreground-500">
                  <span className="flex items-center gap-1">
                    <span className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-user-line text-xs"></i>
                    </span>
                    {article.author}
                  </span>
                  <span className="flex items-center gap-1">
                    <span className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-calendar-line text-xs"></i>
                    </span>
                    {article.date}
                  </span>
                  <span className="flex items-center gap-1">
                    <span className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-eye-line text-xs"></i>
                    </span>
                    {article.readCount.toLocaleString()} 次阅读
                  </span>
                </div>
              </header>

              <div
                className="prose prose-invert max-w-none mb-12
                  prose-headings:text-foreground-950 prose-headings:font-heading
                  prose-h2:text-xl prose-h2:mt-8 prose-h2:mb-4
                  prose-p:text-foreground-600 prose-p:leading-relaxed prose-p:mb-5 prose-p:text-sm md:prose-p:text-base
                  prose-strong:text-foreground-800"
                dangerouslySetInnerHTML={{ __html: article.content }}
              />

              {relatedMovies.length > 0 && (
                <div className="border-t border-background-200/60 pt-8 pb-12">
                  <h3 className="text-lg font-heading text-foreground-950 mb-4">相关电影推荐</h3>
                  <div className="flex gap-4 overflow-x-auto pb-3">
                    {relatedMovies.map(movie => (
                      <MovieCard key={movie.id} movie={movie} size="small" />
                    ))}
                  </div>
                </div>
              )}
            </div>
          </article>
        </main>
        <Footer />
      </div>
    </>
  );
}